/**
 * PipelineVentas — vista principal del usuario ventas.
 *
 * Kanban full-screen que muestra todos los estados del flujo de pedidos.
 * Se adapta automáticamente a cada depósito:
 *   · R8      — flujo completo con asignación de operario
 *   · ISABELA  — sin asignación, estados simplificados + entregados desde pedidos_despachados
 *
 * Columnas con fondo coloreado = requieren acción de VENTAS (despachar, verificar error).
 */

import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  collection, onSnapshot, query,
  orderBy, where, limit,
} from "firebase/firestore";
import { db } from "../../../firebase";
import { useAuth } from "../../../context/AuthContext";
import { useApp } from "../../../context/AppContext";
import { useNavigate } from "react-router-dom";
import { syncPendientesDeHoy, syncRapida } from "../services/syncFinnegans";
import { FLUJO_ISABELA, FLUJO_R8 } from "../services/estadosConfig";
import { esPedidoComponentes } from "../services/parseDescripcionPedido";
import AsignarDepositoModal from "../components/AsignarDepositoModal";
import { bucketFecha, BUCKET_ORDER } from "../services/agrupaciones";

// Aliases locales para mantener los nombres que usa este componente
const STATES_ISABELA = FLUJO_ISABELA;
const STATES_R8      = FLUJO_R8;

// ─────────────────────────────────────────────────────────────
//  Helpers
// ─────────────────────────────────────────────────────────────

const STUCK_MS = 2 * 24 * 3600 * 1000;

// Estados donde ventas puede reasignar el depósito (antes de que el operario prepare el pedido)
const ESTADOS_REASIGNABLES = new Set(["PENDIENTE_ASIGNAR", "ASIGNADO", "EN_PREPARACION", "CON_ERROR", "ERROR_CONFIRMADO"]);

function isStuck(p) {
  if (!p.updatedAt?.seconds) return false;
  const final = ["DESPACHADO", "CANCELADO", "ANULADO", "DESP_ENTREGADO"];
  if (final.includes(p.estado)) return false;
  return Date.now() - p.updatedAt.seconds * 1000 > STUCK_MS;
}

function formatTimeAgo(ts) {
  if (!ts) return null;
  const d = ts?.seconds ? new Date(ts.seconds * 1000) : (ts instanceof Date ? ts : new Date(ts));
  if (isNaN(d)) return null;
  const m = Math.floor((Date.now() - d.getTime()) / 60000);
  if (m < 1) return "ahora";
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}

function getPedidoDate(p) {
  if (p?.finFechaTS?.seconds) return new Date(p.finFechaTS.seconds * 1000);
  if (p?.finFecha) return new Date(`${p.finFecha}T00:00:00`);
  return null;
}

function toDate(ts) {
  if (!ts) return null;
  if (ts?.seconds) return new Date(ts.seconds * 1000);
  const d = ts instanceof Date ? ts : new Date(ts);
  return isNaN(d.getTime()) ? null : d;
}

// Fecha en que el pedido entró a la columna/estado (estadoId) en que se está mostrando
function getEstadoColDate(p, estadoId) {
  if (estadoId === "DESP_PENDIENTE" || estadoId === "DESPACHADO") {
    return toDate(p.despachadoAt) || toDate(p.timestamps?.DESPACHADO) || toDate(p.updatedAt) || getPedidoDate(p);
  }
  if (estadoId === "DESP_ENTREGADO" || estadoId === "ENTREGADO") {
    return toDate(p.entregadoAt) || toDate(p.updatedAt) || getPedidoDate(p);
  }
  return toDate(p.timestamps?.[estadoId]) || toDate(p.updatedAt) || getPedidoDate(p);
}

function formatFechaCorta(d) {
  if (!d) return null;
  return d.toLocaleDateString("es-AR", { day: "2-digit", month: "short" });
}

// Resuelve el nombre del responsable/operario asignado al pedido
function getResponsable(p) {
  return (
    p?.asignadoNombre ||
    p?.asignado?.nombre ||
    p?.asignado?.displayName ||
    p?.operarioNombre ||
    p?.responsableNombre ||
    p?.responsable?.nombre ||
    null
  );
}

// ─────────────────────────────────────────────────────────────
//  PipelineCard
// ─────────────────────────────────────────────────────────────

function PipelineCard({ p, cfg, onReasignar, pagosSet }) {
  const navigate = useNavigate();
  const stuck = isStuck(p);
  const items = p.productos?.length ?? p.items?.length ?? 0;
  const timeAgo = formatTimeAgo(p.timestamps?.[p.estado] || p.updatedAt);
  const creacion = getPedidoDate(p);
  const responsable = getResponsable(p);
  const puedeReasignar = onReasignar && ESTADOS_REASIGNABLES.has(p.estado);
  const isPagado = !!pagosSet?.has(p.id);

  const metodoColors = {
    AGENCIA: { bg: "#eff6ff", color: "#1d4ed8" },
    RETIRA:  { bg: "#f0fdf4", color: "#15803d" },
    CAMION:  { bg: "#fef3c7", color: "#92400e" },
    GIRA:    { bg: "#fdf4ff", color: "#7e22ce" },
  };
  const mc = metodoColors[p.metodoEntrega] || { bg: "#f1f5f9", color: "#475569" };

  // Ventas solo VE el detalle del pedido (no interactúa con la carga). El "ir a
  // Carga de Camión" es exclusivo del encargado (desde Gestión de Pedidos).
  const href = `/pedidos/${encodeURIComponent(p.id)}`;

  return (
    <div
      onClick={() => navigate(href)}
      style={{
        background: "#fff",
        border: stuck
          ? "2px solid #f97316"
          : `1px solid ${cfg.border}`,
        borderRadius: "10px",
        padding: "10px 12px",
        marginBottom: "7px",
        cursor: "pointer",
        boxShadow: stuck
          ? "0 2px 8px rgba(249,115,22,0.18)"
          : "0 1px 3px rgba(0,0,0,0.05)",
        position: "relative",
        transition: "transform 0.1s, box-shadow 0.1s",
      }}
      onMouseEnter={e => {
        e.currentTarget.style.transform = "translateY(-1px)";
        e.currentTarget.style.boxShadow = stuck
          ? "0 4px 12px rgba(249,115,22,0.25)"
          : "0 4px 10px rgba(0,0,0,0.1)";
      }}
      onMouseLeave={e => {
        e.currentTarget.style.transform = "";
        e.currentTarget.style.boxShadow = stuck
          ? "0 2px 8px rgba(249,115,22,0.18)"
          : "0 1px 3px rgba(0,0,0,0.05)";
      }}
    >
      {/* Badge atascado */}
      {stuck && (
        <div style={{
          position: "absolute", top: -9, right: 8,
          background: "#f97316", color: "#fff",
          fontSize: "0.56rem", fontWeight: 800,
          padding: "2px 7px", borderRadius: "999px",
          letterSpacing: "0.05em", textTransform: "uppercase",
        }}>
          +2 días
        </div>
      )}

      {/* Fila 1: # pedido + tiempo en estado */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "3px" }}>
        <span style={{ fontWeight: 800, fontSize: "0.82rem", color: "#0f172a" }}>
          #{p.numero || p.id}
        </span>
        {timeAgo && (
          <span style={{
            fontSize: "0.65rem",
            color: stuck ? "#f97316" : "#94a3b8",
            fontWeight: stuck ? 700 : 500,
          }}>
            ⏱ {timeAgo}
          </span>
        )}
      </div>

      {/* Fila 2: cliente */}
      <p style={{
        margin: "0 0 5px",
        fontSize: "0.85rem", fontWeight: 600, color: "#1e293b",
        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        lineHeight: 1.3,
      }}>
        {p.cliente || "—"}
      </p>

      {/* Fila 3: ítems + método + fecha creación + depósito */}
      <div style={{ display: "flex", alignItems: "center", gap: "5px", flexWrap: "wrap" }}>
        {items > 0 && (
          <span style={{ fontSize: "0.67rem", color: "#64748b" }}>
            {items} ítem{items !== 1 ? "s" : ""}
          </span>
        )}
        {p.metodoEntrega && (
          <span style={{
            fontSize: "0.6rem", fontWeight: 700,
            background: mc.bg, color: mc.color,
            padding: "1px 6px", borderRadius: "999px",
          }}>
            {p.metodoEntrega}
          </span>
        )}
        {isPagado && (
          <span style={{
            fontSize: "0.58rem", fontWeight: 700,
            background: "#f0fdf4", color: "#15803d",
            padding: "1px 6px", borderRadius: "999px",
            border: "1px solid #86efac",
          }}>
            💳 Pagado
          </span>
        )}
        {/* Responsable / operario asignado */}
        {responsable && (
          <span style={{
            fontSize: "0.6rem", color: "#64748b",
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "100px",
          }}>
            👤 {responsable}
          </span>
        )}
        {/* Fecha de creación (referencia) */}
        {creacion && p.estado !== "PENDIENTE_ASIGNAR" && (
          <span style={{ fontSize: "0.6rem", color: "#94a3b8", marginLeft: "auto" }}>
            📅 {formatFechaCorta(creacion)}
          </span>
        )}
        {/* Depósito — solo cuando se ven todos los depósitos */}
        {p.deposito && !p._hideDeposito && (
          <span style={{
            fontSize: "0.58rem", color: "#94a3b8",
            marginLeft: creacion ? "0" : "auto",
          }}>
            {p.deposito}
          </span>
        )}
      </div>

      {/* Botón reasignar depósito — solo en estados pre-preparado */}
      {puedeReasignar && (
        <button
          onClick={e => { e.stopPropagation(); onReasignar(p); }}
          title="Cambiar depósito o método de entrega"
          style={{
            marginTop: "8px",
            width: "100%",
            padding: "5px 8px",
            border: "1px solid #e2e8f0",
            borderRadius: "6px",
            background: "#f8fafc",
            color: "#64748b",
            fontSize: "0.68rem",
            fontWeight: 600,
            cursor: "pointer",
            textAlign: "center",
            transition: "all 0.12s",
          }}
          onMouseEnter={e => { e.currentTarget.style.background = "#f1f5f9"; e.currentTarget.style.color = "#334155"; }}
          onMouseLeave={e => { e.currentTarget.style.background = "#f8fafc"; e.currentTarget.style.color = "#64748b"; }}
        >
          ↕ Cambiar destino
        </button>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
//  PipelineColumn
// ─────────────────────────────────────────────────────────────

function PipelineColumn({ cfg, items, colHeight, onReasignar, pagosSet, labelOverride, groupedInner }) {
  const stuckCount = items.filter(isStuck).length;

  // Agrupar por bucket de fecha (HOY/AYER/...) según cuándo entraron a este estado
  const porFecha = {};
  for (const p of items) {
    const bucket = bucketFecha(getEstadoColDate(p, cfg.id));
    (porFecha[bucket] ||= []).push(p);
  }
  const bucketsConItems = BUCKET_ORDER.filter(b => (porFecha[b] || []).length > 0);

  return (
    <div style={{
      display: "flex",
      flexDirection: "column",
      background: "#fff",
      borderRadius: groupedInner ? "10px" : "14px",
      border: groupedInner
        ? "1px solid #cbd5e1"
        : (cfg.ventasAction ? `2px solid ${cfg.border}` : "1.5px solid #e2e8f0"),
      overflow: "hidden",
      boxShadow: (!groupedInner && cfg.ventasAction)
        ? `0 0 0 3px ${cfg.border}40`
        : "none",
      /* Columna crece para ocupar todo el alto disponible */
      minHeight: groupedInner ? "100px" : "120px",
      height: colHeight,
    }}>

      {/* Cabecera de columna */}
      <div style={{
        background: cfg.ventasAction ? cfg.headerBg : "#f8fafc",
        borderBottom: `1px solid ${cfg.ventasAction ? cfg.border : "#f1f5f9"}`,
        padding: "11px 13px 10px",
        flexShrink: 0,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: cfg.ventasAction || cfg.sublabel ? "4px" : 0 }}>
          <span style={{ fontSize: "0.88rem", lineHeight: 1 }}>{cfg.icon}</span>
          <span style={{
            flex: 1,
            fontWeight: 700,
            fontSize: "0.78rem",
            color: cfg.ventasAction ? cfg.color : "#475569",
            letterSpacing: "-0.01em",
          }}>
            {labelOverride || cfg.label}
          </span>
          {/* Badge atascados en columna */}
          {stuckCount > 0 && (
            <span style={{
              background: "#f97316", color: "#fff",
              fontSize: "0.58rem", fontWeight: 800,
              padding: "1px 5px", borderRadius: "999px",
            }}>
              ⚠{stuckCount}
            </span>
          )}
          {/* Contador de pedidos */}
          <span style={{
            background: items.length > 0
              ? cfg.color + "22"
              : "#f1f5f9",
            color: items.length > 0 ? cfg.color : "#94a3b8",
            fontSize: "0.72rem", fontWeight: 700,
            padding: "1px 8px", borderRadius: "999px",
            minWidth: "22px", textAlign: "center",
          }}>
            {items.length}
          </span>
        </div>
        {/* Sub-label para columnas con acción de ventas */}
        {cfg.sublabel && (
          <p style={{
            margin: 0,
            fontSize: "0.6rem",
            fontWeight: 700,
            color: cfg.ventasAction ? cfg.color : "#64748b",
            textTransform: "uppercase",
            letterSpacing: "0.06em",
          }}>
            {cfg.ventasAction ? "▶ " : ""}{cfg.sublabel}
          </p>
        )}
      </div>

      {/* Cuerpo scrolleable */}
      <div style={{
        flex: 1,
        overflowY: "auto",
        padding: "9px",
        background: items.length === 0 ? "#fafafa" : "transparent",
      }}>
        {items.length === 0 ? (
          <p style={{
            textAlign: "center",
            color: "#d1d5db",
            fontSize: "0.75rem",
            padding: "16px 0",
            margin: 0,
          }}>
            Sin pedidos
          </p>
        ) : (
          bucketsConItems.map(bucket => (
            <div key={bucket} style={{ marginBottom: "8px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "6px", padding: "2px 0 5px" }}>
                <span style={{ fontSize: "0.6rem", fontWeight: 700, color: cfg.color, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                  {bucket}
                </span>
                <div style={{ flex: 1, height: "1px", background: cfg.border || "#e2e8f0" }} />
                <span style={{ fontSize: "0.6rem", fontWeight: 700, color: cfg.color }}>
                  {porFecha[bucket].length}
                </span>
              </div>
              {porFecha[bucket].map(p => (
                <PipelineCard key={p.id} p={p} cfg={cfg} onReasignar={onReasignar} pagosSet={pagosSet} />
              ))}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
//  Componente principal
// ─────────────────────────────────────────────────────────────

export default function PipelineVentas() {
  const { profile } = useAuth();
  const { toast } = useApp();
  const navigate = useNavigate();

  // Si el usuario tiene un depósito asignado en su perfil, lo usamos como filtro inicial
  // Normalizamos a UPPERCASE para que coincida con los chips de depósito
  const depositoInicial = profile?.deposito ? String(profile.deposito).trim().toUpperCase() : "";

  const [pedidos, setPedidos]               = useState([]);
  const [despachadosAll, setDespachadosAll] = useState([]);
  const [entregadosAll, setEntregadosAll]   = useState([]);
  const [pagosPendientes, setPagosPendientes] = useState(0);
  const [pagosSet,      setPagosSet]        = useState(new Set());
  const [sinDeposito,   setSinDeposito]     = useState(0);
  const [loading, setLoading]               = useState(true);
  const [searchQ, setSearchQ]               = useState("");
  const [filterDeposito, setFilterDeposito] = useState(depositoInicial);
  const [reasignando, setReasignando]       = useState(null); // pedido a reasignar
  const [isMobile, setIsMobile]             = useState(() => window.innerWidth < 768);

  // ── Detectar mobile/desktop (resizable) ──
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  // ── Detección de rol ──
  // Chequeamos ambos campos para cubrir perfiles con role/rol mixtos
  const _roleField = (profile?.role || "").toLowerCase();
  const _rolField  = (profile?.rol  || "").toLowerCase();
  const _role      = _roleField || _rolField;
  // soloLectura: visor puede ver todo pero no actuar (en cualquiera de los dos campos)
  const soloLectura = _roleField === "visor" || _rolField === "visor";
  // puedeReasignarDeposito: solo ventas y admin pueden cambiar el destino de un pedido
  const puedeReasignarDeposito = (_role === "ventas" || _role === "admin") && !soloLectura;
  const [syncing, setSyncing]               = useState(false);
  const [lastSync, setLastSync]             = useState(null);
  const syncRunRef                          = useRef(false);

  // ── Auto-sync al entrar a la página (una vez por sesión) ──
  useEffect(() => {
    if (syncRunRef.current) return;
    syncRunRef.current = true;
    (async () => {
      setSyncing(true);
      try { await syncPendientesDeHoy(); setLastSync(new Date()); }
      catch (e) { console.error("[PipelineVentas] sync auto error:", e); }
      finally { setSyncing(false); }
    })();
  }, []);

  async function handleSync() {
    if (syncing) return;
    setSyncing(true);
    try {
      await syncRapida(); // función del servidor (batcheada) con fallback client-side
      setLastSync(new Date());
      toast.success("Pedidos actualizados ✓");
    } catch (e) {
      console.error(e);
      toast.error("No se pudieron sincronizar los pedidos");
    } finally {
      setSyncing(false);
    }
  }

  // Medir el TopBar para calcular la altura disponible exacta
  const [topbarH, setTopbarH] = useState(60);
  useEffect(() => {
    const measure = () => {
      const tb = document.querySelector(".topbar");
      if (tb) setTopbarH(tb.offsetHeight);
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  // ── Listener pedidos activos (colección principal) ──
  // Cuando hay un depósito seleccionado usamos where("deposito", ==) igual que el
  // encargado — esto garantiza reactividad en tiempo real sin depender de índices
  // compuestos ni de que updatedAt ya esté confirmado por el servidor.
  // Cuando se ve "Todos" usamos orderBy + limit como antes.
  useEffect(() => {
    setLoading(true);
    let q;
    if (filterDeposito) {
      q = query(
        collection(db, "pedidos"),
        where("deposito", "==", filterDeposito)
      );
    } else {
      q = query(
        collection(db, "pedidos"),
        orderBy("updatedAt", "desc"),
        limit(500)
      );
    }
    const unsub = onSnapshot(q,
      snap => {
        setPedidos(
          snap.docs
            .map(d => ({ id: d.id, ...d.data() }))
            .filter(p => !["CANCELADO", "ANULADO"].includes(p.estado))
        );
        setLoading(false);
      },
      err => { console.error("[PipelineVentas]", err); setLoading(false); }
    );
    return () => unsub();
  }, [filterDeposito]); // ← reactivo: se re-subscribe cuando cambia el depósito

  // ── Listener pedidos_despachados ──
  // Se usan DOS listeners:
  //   1. ISABELA: sin filtro de fecha — muestra TODOS los pendientes de entrega sin importar cuándo se despacharon
  //   2. R8: últimos 30 días — solo para la columna "Despachados hoy"
  useEffect(() => {
    // Listener ISABELA — sin restricción de fecha, todos los despachados de ese depósito
    // Sin orderBy para evitar requerir índice compuesto; el orden se aplica en JS (byEstado)
    const qIsabela = query(
      collection(db, "pedidos_despachados"),
      where("deposito", "==", "ISABELA"),
      limit(500)
    );

    // Listener R8 — últimos 30 días
    const since = new Date();
    since.setDate(since.getDate() - 30);
    since.setHours(0, 0, 0, 0);
    const qR8 = query(
      collection(db, "pedidos_despachados"),
      where("despachadoAt", ">=", since),
      orderBy("despachadoAt", "desc"),
      limit(300)
    );

    // Mantenemos dos Sets para combinarlos sin duplicados
    let isabelaDocs = [];
    let r8Docs      = [];

    function merge() {
      const seen = new Set();
      const combined = [];
      for (const d of [...isabelaDocs, ...r8Docs]) {
        if (!seen.has(d.id)) { seen.add(d.id); combined.push(d); }
      }
      setDespachadosAll(combined);
    }

    const unsubIsabela = onSnapshot(qIsabela,
      snap => { isabelaDocs = snap.docs.map(d => ({ id: d.id, ...d.data() })); merge(); },
      err  => console.error("[PipelineVentas] despachos ISABELA:", err)
    );
    const unsubR8 = onSnapshot(qR8,
      snap => { r8Docs = snap.docs.map(d => ({ id: d.id, ...d.data() })); merge(); },
      err  => console.error("[PipelineVentas] despachos R8:", err)
    );

    return () => { unsubIsabela(); unsubR8(); };
  }, []);

  // ── Listener pedidos_entregados — solo HOY para el pipeline ──
  // El histórico completo está en /ventas/entregados
  useEffect(() => {
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    const q = query(
      collection(db, "pedidos_entregados"),
      where("entregadoAt", ">=", hoy),
      limit(300)
    );
    const unsub = onSnapshot(q,
      snap => setEntregadosAll(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
      err  => console.error("[PipelineVentas] entregados:", err)
    );
    return () => unsub();
  }, []);

  // ── Listener pagos — contador de pendientes + Set de pedidosId con pago ──
  // Solo para ventas/admin (respeta la regla de Firestore). Se espera a que el perfil esté cargado.
  useEffect(() => {
    const rf = (profile?.role || "").toLowerCase();
    const rl = (profile?.rol  || "").toLowerCase();
    const role = rf || rl;
    const isVisorUser = rf === "visor" || rl === "visor";
    if (!role || (!isVisorUser && role !== "ventas" && role !== "admin")) return;

    // Escuchamos TODOS los pagos para construir el Set de pedidoIds pagados
    const q = query(collection(db, "pagos"));
    const unsub = onSnapshot(q,
      snap => {
        let pendientes = 0;
        const ids = new Set();
        snap.forEach(d => {
          ids.add(d.id); // el doc id ES el pedidoId
          if (d.data().pagoEstado === "PENDIENTE") pendientes++;
        });
        setPagosSet(ids);
        setPagosPendientes(pendientes);
      },
      err  => console.error("[PipelineVentas] pagos:", err)
    );
    return () => unsub();
  }, [profile?.role, profile?.rol]);

  // ── Listener: conteo de pedidos sin depósito asignado ──
  useEffect(() => {
    const EXCLUIDOS = ["CANCELADO", "ANULADO", "DESPACHADO", "ENTREGADO"];
    let nullCount = 0, emptyCount = 0;
    function update() {
      setSinDeposito(nullCount + emptyCount);
    }
    const qNull = query(collection(db, "pedidos"), where("deposito", "==", null), limit(500));
    const qEmpty = query(collection(db, "pedidos"), where("deposito", "==", ""), limit(500));
    const unsubNull = onSnapshot(qNull,
      snap => { nullCount  = snap.docs.filter(d => !EXCLUIDOS.includes(d.data().estado)).length; update(); },
      err  => console.error("[PipelineVentas] sinDeposito null:", err)
    );
    const unsubEmpty = onSnapshot(qEmpty,
      snap => { emptyCount = snap.docs.filter(d => !EXCLUIDOS.includes(d.data().estado)).length; update(); },
      err  => console.error("[PipelineVentas] sinDeposito empty:", err)
    );
    return () => { unsubNull(); unsubEmpty(); };
  }, []);

  // ── Depósitos disponibles ──
  // Base fija para que los chips siempre aparezcan aunque no haya pedidos activos.
  // Se complementa con cualquier depósito extra que aparezca en Firestore.
  const DEPOSITOS_BASE = ["ISABELA", "R8"];
  const deposits = useMemo(() => {
    const fromPedidos = pedidos
      .map(p => p.deposito ? String(p.deposito).trim().toUpperCase() : null)
      .filter(Boolean);
    return [...new Set([...DEPOSITOS_BASE, ...fromPedidos])].sort();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pedidos]);

  // ── ¿Estamos viendo ISABELA? ──
  const isIsabela = filterDeposito.toUpperCase() === "ISABELA";

  // Config de estados según depósito seleccionado.
  // Para visor (soloLectura) quitamos los colores de acción — no tiene nada que hacer.
  const stateConfig = useMemo(() => {
    let base;
    if (isIsabela) {
      base = STATES_ISABELA;
    } else {
      // R8 — columnas achicadas para VENTAS (no ve ASIGNADO ni CON_ERROR sin
      // revisar; el despachado se separa por método: entregar vs cargar camión).
      const byId = Object.fromEntries(STATES_R8.map(c => [c.id, c]));
      const desp = byId["DESPACHADO"] || {};
      base = [
        byId["PENDIENTE_ASIGNAR"],
        byId["EN_PREPARACION"],
        byId["ERROR_CONFIRMADO"],
        byId["PREPARADO"],
        byId["CONTROLADO"],
        { ...desp, id: "DESPACHADO", label: "Despachado · entregar", sublabel: "Retira" },
        { ...desp, id: "DESPACHADO_CARGA", label: "Despachado · cargar camión", sublabel: "Agencia / camión", icon: "🚚", color: "#1d4ed8", bg: "#eff6ff", border: "#bfdbfe", headerBg: "#dbeafe" },
        { ...desp, id: "EN_CAMION", label: "En el camión", sublabel: "Agencia / camión cargado", icon: "🚛", color: "#0369a1", bg: "#f0f9ff", border: "#bae6fd", headerBg: "#e0f2fe" },
        byId["ENTREGADO"],
      ].filter(Boolean);
    }
    if (!soloLectura) return base;
    return base.map(cfg => ({ ...cfg, ventasAction: false }));
  }, [isIsabela, soloLectura]);

  // ── Filtro base de pedidos ──
  const pedidosFiltrados = useMemo(() => {
    const nq = searchQ.trim().toLowerCase();
    return pedidos.filter(p => {
      // Excluir pedidos de componentes de kit (referencia, no operativos)
      if (esPedidoComponentes(p)) return false;
      // Filtro por depósito: comparación case-insensitive y trim para evitar falsos negativos
      if (filterDeposito) {
        const dep = String(p.deposito || "").trim().toUpperCase();
        if (dep !== filterDeposito.trim().toUpperCase()) return false;
      }
      if (nq) {
        const num = String(p.numero || p.id || "").toLowerCase();
        const cli = String(p.cliente || "").toLowerCase();
        return num.includes(nq) || cli.includes(nq);
      }
      return true;
    }).map(p => ({ ...p, _hideDeposito: !!filterDeposito }));
  }, [pedidos, searchQ, filterDeposito]);

  // ── Filtro base de despachados ──
  const despsFiltradas = useMemo(() => {
    const nq = searchQ.trim().toLowerCase();
    return despachadosAll.filter(p => {
      if (filterDeposito) {
        const dep = String(p.deposito || "").trim().toUpperCase();
        if (dep !== filterDeposito.trim().toUpperCase()) return false;
      }
      if (nq) {
        const num = String(p.numero || p.id || "").toLowerCase();
        const cli = String(p.cliente || "").toLowerCase();
        return num.includes(nq) || cli.includes(nq);
      }
      return true;
    });
  }, [despachadosAll, searchQ, filterDeposito]);

  // ── Set de números de pedido que siguen activos en 'pedidos' (no terminados) ──
  // Evita que un pedido aparezca a la vez en 'Para despachar' y en las columnas
  // de pedidos_despachados cuando la operación de despacho quedó incompleta.
  const ESTADOS_TERMINADOS = new Set(["DESPACHADO", "ANULADO", "CANCELADO"]);
  const activosNums = useMemo(() => {
    const s = new Set();
    for (const p of pedidos) {
      if (!ESTADOS_TERMINADOS.has(p.estado)) {
        const num = String(p.numero || p.id || "").trim().toLowerCase();
        if (num) s.add(num);
      }
    }
    return s;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pedidos]);

  // Filtra despsFiltradas excluyendo pedidos que todavía están activos en 'pedidos'
  const despsNoActivos = useMemo(() =>
    despsFiltradas.filter(p => {
      const num = String(p.numero || p.id || "").trim().toLowerCase();
      return !activosNums.has(num);
    })
  , [despsFiltradas, activosNums]);

  // ── Pedidos de hoy (para columna DESPACHADO de R8) ──
  const despachosHoy = useMemo(() => {
    const hoy = new Date(); hoy.setHours(0, 0, 0, 0);
    return despsNoActivos.filter(p => {
      if (!p.despachadoAt?.seconds) return false;
      return new Date(p.despachadoAt.seconds * 1000) >= hoy;
    }).map(p => ({ ...p, estado: "DESPACHADO", _hideDeposito: !!filterDeposito }));
  }, [despsNoActivos, filterDeposito]);

  // ── Virtuales ISABELA ──
  const isabelaPendienteEntrega = useMemo(() =>
    despsNoActivos
      .filter(p => p.necesitaControl !== false)
      .map(p => ({ ...p, estado: "DESP_PENDIENTE", _hideDeposito: !!filterDeposito }))
  , [despsNoActivos, filterDeposito]);

  // ── Helper: filtra entregadosAll por depósito, búsqueda y fecha (solo HOY) ──
  const entregadosFiltrados = useMemo(() => {
    const nq = searchQ.trim().toLowerCase();
    const hoyMs = (() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d.getTime(); })();

    return entregadosAll.filter(p => {
      // Sólo pedidos entregados HOY — si no tiene fecha válida de hoy, no aparece en pipeline
      const ts = p.entregadoAt?.seconds
        ? p.entregadoAt.seconds * 1000
        : p.entregadoAt instanceof Date
          ? p.entregadoAt.getTime()
          : typeof p.entregadoAt === "number"
            ? p.entregadoAt
            : null;
      if (ts === null || ts < hoyMs) return false;

      if (filterDeposito) {
        const dep = String(p.deposito || "").trim().toUpperCase();
        if (dep !== filterDeposito.trim().toUpperCase()) return false;
      }
      if (nq) {
        const num = String(p.numero || p.id || "").toLowerCase();
        const cli = String(p.cliente || "").toLowerCase();
        return num.includes(nq) || cli.includes(nq);
      }
      return true;
    });
  }, [entregadosAll, searchQ, filterDeposito]);

  // ISABELA → estado virtual "DESP_ENTREGADO"
  const isabelaEntregados = useMemo(() =>
    entregadosFiltrados.map(p => ({ ...p, estado: "DESP_ENTREGADO", _hideDeposito: !!filterDeposito }))
  , [entregadosFiltrados, filterDeposito]);

  // R8 → estado virtual "ENTREGADO"
  const r8Entregados = useMemo(() =>
    entregadosFiltrados.map(p => ({ ...p, estado: "ENTREGADO", _hideDeposito: !!filterDeposito }))
  , [entregadosFiltrados, filterDeposito]);

  // ── Agrupación por estado → columna ──
  const byEstado = useMemo(() => {
    const out = {};
    for (const cfg of stateConfig) out[cfg.id] = [];

    for (const p of pedidosFiltrados) {
      let e = p.estado || "PENDIENTE_ASIGNAR";
      // Ventas: ASIGNADO se muestra dentro de "En preparación" (no tiene columna propia).
      if (!isIsabela && e === "ASIGNADO") e = "EN_PREPARACION";
      if (out[e] !== undefined) out[e].push(p);
    }

    // Columnas derivadas de pedidos_despachados / pedidos_entregados
    if (isIsabela) {
      // ISABELA — DESP_PENDIENTE: pedidos_despachados + fallback desde colección pedidos
      const despFromPedidos = pedidosFiltrados
        .filter(p => p.estado === "DESPACHADO")
        .map(p => ({ ...p, estado: "DESP_PENDIENTE", _hideDeposito: !!filterDeposito }));
      const idsFromDespachos = new Set(isabelaPendienteEntrega.map(p => p.id));
      out["DESP_PENDIENTE"] = [
        ...isabelaPendienteEntrega,
        ...despFromPedidos.filter(p => !idsFromDespachos.has(p.id)),
      ];
      out["DESP_ENTREGADO"] = isabelaEntregados;
    } else {
      // R8 — DESPACHADO: pendientes de su entrega/carga (necesitaControl !== false),
      // excluyendo los ya cargados (CARGADO) o entregados (ENTREGADO).
      // Se SEPARA por método: RETIRA → entregar; agencia/camión → cargar camión.
      const r8Pend = despsNoActivos
        .filter(p => p.necesitaControl !== false && p.cargaEstado !== "CARGADO" && p.cargaEstado !== "ENTREGADO");
      const despFromPedidosR8 = pedidosFiltrados
        .filter(p => p.estado === "DESPACHADO");
      const idsPend = new Set(r8Pend.map(p => p.id));
      const todosDesp = [...r8Pend, ...despFromPedidosR8.filter(p => !idsPend.has(p.id))];

      const esRetira = p => p.metodoEntrega === "RETIRA";
      out["DESPACHADO"] = todosDesp
        .filter(esRetira)
        .map(p => ({ ...p, estado: "DESPACHADO", _hideDeposito: !!filterDeposito }));
      out["DESPACHADO_CARGA"] = todosDesp
        .filter(p => !esRetira(p))
        .map(p => ({ ...p, estado: "DESPACHADO_CARGA", _hideDeposito: !!filterDeposito }));
      // R8 — EN_CAMION: agencia/camión ya cargados (cargaEstado CARGADO) pero aún no entregados.
      // Antes desaparecían de la vista de ventas; ahora quedan visibles acá.
      out["EN_CAMION"] = despsNoActivos
        .filter(p => p.cargaEstado === "CARGADO" && p.metodoEntrega !== "RETIRA")
        .map(p => ({ ...p, estado: "EN_CAMION", _hideDeposito: !!filterDeposito }));
      // R8 — ENTREGADO: desde pedidos_entregados
      out["ENTREGADO"] = r8Entregados;
    }

    // Ordenar cada columna: atascados primero, luego por fecha de ingreso al estado (desc)
    for (const key of Object.keys(out)) {
      out[key].sort((a, b) => {
        if (isStuck(a) !== isStuck(b)) return isStuck(a) ? -1 : 1;
        return (getEstadoColDate(b, key)?.getTime() ?? 0) - (getEstadoColDate(a, key)?.getTime() ?? 0);
      });
    }

    return out;
  }, [pedidosFiltrados, stateConfig, isIsabela, isabelaPendienteEntrega, isabelaEntregados, r8Entregados, despsNoActivos, despachosHoy, filterDeposito]);

  // ── Totales para el header ──
  const totalPedidos = pedidosFiltrados.length;
  const totalAtascados = pedidosFiltrados.filter(isStuck).length;
  const totalAccion = useMemo(() =>
    stateConfig
      .filter(c => c.ventasAction)
      .reduce((acc, c) => acc + (byEstado[c.id]?.length || 0), 0)
  , [stateConfig, byEstado]);

  // ── Altura disponible: viewport - TopBar (medido dinámicamente) ──
  // Pipeline header interno: 58px | Grid padding top+bottom: 20px
  const availH    = `calc(100vh - ${topbarH}px)`;
  const colHeight = `calc(100vh - ${topbarH}px - 58px - 20px)`;

  // ── Unidades de render: DESPACHADO + DESPACHADO_CARGA se agrupan bajo "Despachado" ──
  const pipelineUnits = useMemo(() => {
    const units = [];
    const cargar = stateConfig.find(c => c.id === "DESPACHADO_CARGA");
    const entregar = stateConfig.find(c => c.id === "DESPACHADO");
    const puedeAgrupar = !!cargar && !!entregar; // solo R8
    let groupInserted = false;
    for (const cfg of stateConfig) {
      if (puedeAgrupar && (cfg.id === "DESPACHADO" || cfg.id === "DESPACHADO_CARGA")) {
        if (!groupInserted) { units.push({ type: "group", cargar, entregar }); groupInserted = true; }
        continue;
      }
      units.push({ type: "single", cfg });
    }
    return units;
  }, [stateConfig]);

  // ── Render de una sección mobile (reutilizable para single y sub-columnas del grupo) ──
  function renderMobileSection(cfg, { labelOverride, forceShow } = {}) {
    const items = byEstado[cfg.id] || [];
    if (items.length === 0 && !cfg.ventasAction && !forceShow) return null;
    const porFechaMobile = {};
    for (const p of items) {
      const bucket = bucketFecha(getEstadoColDate(p, cfg.id));
      (porFechaMobile[bucket] ||= []).push(p);
    }
    const bucketsConItemsMobile = BUCKET_ORDER.filter(b => (porFechaMobile[b] || []).length > 0);
    return (
      <div key={cfg.id} style={{ marginBottom: "16px" }}>
        <div style={{
          display: "flex", alignItems: "center", gap: "6px",
          padding: "7px 10px",
          background: cfg.ventasAction ? cfg.headerBg : "#f8fafc",
          border: `1px solid ${cfg.ventasAction ? cfg.border : "#e2e8f0"}`,
          borderRadius: items.length > 0 ? "10px 10px 0 0" : "10px",
          borderBottom: items.length > 0 ? "none" : undefined,
        }}>
          <span style={{ fontSize: "0.85rem", lineHeight: 1 }}>{cfg.icon}</span>
          <span style={{ flex: 1, fontWeight: 700, fontSize: "0.78rem", color: cfg.ventasAction ? cfg.color : "#475569" }}>
            {labelOverride || cfg.label}
          </span>
          <span style={{
            background: items.length > 0 ? cfg.color + "22" : "#f1f5f9",
            color: items.length > 0 ? cfg.color : "#94a3b8",
            fontSize: "0.7rem", fontWeight: 700, padding: "1px 8px", borderRadius: "999px",
          }}>
            {items.length}
          </span>
        </div>
        {items.length > 0 ? (
          <div style={{
            border: `1px solid ${cfg.ventasAction ? cfg.border : "#e2e8f0"}`,
            borderTop: "none", borderRadius: "0 0 10px 10px",
            background: "#fff", padding: "8px",
            display: "flex", flexDirection: "column", gap: "6px",
          }}>
            {bucketsConItemsMobile.map(bucket => (
              <div key={bucket}>
                <div style={{ display: "flex", alignItems: "center", gap: "6px", padding: "2px 0 5px" }}>
                  <span style={{ fontSize: "0.6rem", fontWeight: 700, color: cfg.color, textTransform: "uppercase", letterSpacing: "0.06em" }}>{bucket}</span>
                  <div style={{ flex: 1, height: "1px", background: cfg.border || "#e2e8f0" }} />
                  <span style={{ fontSize: "0.6rem", fontWeight: 700, color: cfg.color }}>{porFechaMobile[bucket].length}</span>
                </div>
                {porFechaMobile[bucket].map(p => (
                  <PipelineCard key={p.id} p={p} cfg={cfg} onReasignar={puedeReasignarDeposito ? setReasignando : null} pagosSet={pagosSet} />
                ))}
              </div>
            ))}
          </div>
        ) : forceShow ? (
          <div style={{ border: "1px solid #e2e8f0", borderTop: "none", borderRadius: "0 0 10px 10px", background: "#fafafa", padding: "10px", textAlign: "center", color: "#cbd5e1", fontSize: "0.75rem" }}>
            Sin pedidos
          </div>
        ) : null}
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────
  //  Render
  // ─────────────────────────────────────────────────────────────

  return (
    <div style={{
      background: "#f1f5f9",
      height: availH,
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
    }}>

      {/* ── Header compacto ── */}
      <div style={{
        background: "#fff",
        borderBottom: "1px solid #e2e8f0",
        padding: "0 20px",
        height: "58px",
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        gap: "14px",
        zIndex: 10,
      }}>

        {/* Título + badges */}
        <h1 style={{ margin: 0, fontSize: "1rem", fontWeight: 800, color: "#0f172a", whiteSpace: "nowrap" }}>
          Pedidos
        </h1>

        {/* Badge: pedidos con acción */}
        {!loading && totalAccion > 0 && (
          <span style={{
            background: "#15803d", color: "#fff",
            fontSize: "0.7rem", fontWeight: 800,
            padding: "3px 10px", borderRadius: "999px",
            whiteSpace: "nowrap", flexShrink: 0,
          }}>
            {totalAccion} para despachar
          </span>
        )}

        {/* Badge: atascados */}
        {!loading && totalAtascados > 0 && (
          <span style={{
            background: "#f97316", color: "#fff",
            fontSize: "0.7rem", fontWeight: 800,
            padding: "3px 10px", borderRadius: "999px",
            whiteSpace: "nowrap", flexShrink: 0,
          }}>
            ⚠️ {totalAtascados} atascado{totalAtascados !== 1 ? "s" : ""}
          </span>
        )}

        <div style={{ flex: 1 }} />

        {/* Chips de depósito */}
        <div style={{ display: "flex", gap: "5px", flexShrink: 0 }}>
          {["", ...deposits].map(dep => {
            const active = filterDeposito === dep;
            return (
              <button
                key={dep || "__todos__"}
                onClick={() => setFilterDeposito(dep)}
                style={{
                  padding: "4px 12px",
                  borderRadius: "999px",
                  border: "none",
                  background: active ? "#0f172a" : "#f1f5f9",
                  color: active ? "#fff" : "#64748b",
                  fontSize: "0.74rem",
                  fontWeight: active ? 700 : 500,
                  cursor: "pointer",
                  transition: "all 0.1s",
                }}
              >
                {dep || "Todos"}
              </button>
            );
          })}
        </div>

        {/* Búsqueda */}
        <input
          placeholder="🔍 Buscar cliente o #pedido…"
          value={searchQ}
          onChange={e => setSearchQ(e.target.value)}
          style={{
            padding: "6px 12px",
            borderRadius: "8px",
            border: "1.5px solid #e2e8f0",
            fontSize: "0.83rem",
            width: "210px",
            background: "#f8fafc",
            flexShrink: 0,
          }}
        />

        {/* Total pedidos */}
        {!loading && (
          <span style={{ fontSize: "0.72rem", color: "#94a3b8", whiteSpace: "nowrap", flexShrink: 0 }}>
            {totalPedidos} pedido{totalPedidos !== 1 ? "s" : ""}
          </span>
        )}

        {/* Botón sync Finnegans */}
        <button
          onClick={handleSync}
          disabled={syncing}
          title={lastSync
            ? `Última actualización: ${lastSync.toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit" })}`
            : "Actualizar pedidos desde Finnegans"}
          style={{
            display: "flex", alignItems: "center", justifyContent: "center",
            gap: "5px",
            height: "32px",
            padding: "0 12px",
            background: syncing ? "#f1f5f9" : "#0f172a",
            border: "none",
            borderRadius: "8px",
            color: syncing ? "#94a3b8" : "#fff",
            fontSize: "0.76rem",
            fontWeight: 700,
            cursor: syncing ? "not-allowed" : "pointer",
            flexShrink: 0,
            transition: "all 0.15s",
          }}
        >
          <span style={{
            display: "inline-block",
            animation: syncing ? "spin 0.8s linear infinite" : "none",
          }}>
            🔄
          </span>
          {syncing ? "Actualizando…" : "Actualizar"}
          {lastSync && !syncing && (
            <span style={{ fontSize: "0.65rem", fontWeight: 400, opacity: 0.7, marginLeft: "2px" }}>
              {lastSync.toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit" })}
            </span>
          )}
        </button>

        {/* Link al histórico de despachos */}
        <a
          href="/ventas/despachados"
          title="Histórico de despachos"
          style={{
            display: "flex", alignItems: "center", justifyContent: "center",
            width: "32px", height: "32px",
            background: "#f8fafc", border: "1px solid #e2e8f0",
            borderRadius: "8px", textDecoration: "none",
            fontSize: "0.9rem", flexShrink: 0,
          }}
        >
          📋
        </a>

        {/* Link al histórico de entregados */}
        <a
          href="/ventas/entregados"
          title="Histórico de pedidos entregados"
          style={{
            display: "flex", alignItems: "center", justifyContent: "center",
            gap: "5px",
            height: "32px",
            padding: "0 11px",
            background: "#f0fdf4", border: "1px solid #bbf7d0",
            borderRadius: "8px", textDecoration: "none",
            fontSize: "0.74rem", fontWeight: 700,
            color: "#15803d", flexShrink: 0,
            whiteSpace: "nowrap",
          }}
        >
          📦 Entregados
        </a>

        {/* Link al historial unificado de cargas y entregas */}
        <a
          href="/historial-cargas"
          title="Historial de cargas y entregas"
          style={{
            display: "flex", alignItems: "center", justifyContent: "center",
            gap: "5px",
            height: "32px",
            padding: "0 11px",
            background: "#f0f9ff", border: "1px solid #bae6fd",
            borderRadius: "8px", textDecoration: "none",
            fontSize: "0.74rem", fontWeight: 700,
            color: "#0369a1", flexShrink: 0,
            whiteSpace: "nowrap",
          }}
        >
          🚛 Cargas
        </a>

        {/* Link a pedidos sin depósito con badge — solo si puede actuar */}
        {!soloLectura && <a
          href="/ventas/sin-deposito"
          title="Pedidos sin depósito asignado"
          style={{
            display: "flex", alignItems: "center", justifyContent: "center",
            gap: "5px",
            height: "32px",
            padding: "0 11px",
            background: sinDeposito > 0 ? "#fff7ed" : "#f8fafc",
            border: `1px solid ${sinDeposito > 0 ? "#fdba74" : "#e2e8f0"}`,
            borderRadius: "8px", textDecoration: "none",
            fontSize: "0.74rem", fontWeight: 700,
            color: sinDeposito > 0 ? "#c2410c" : "#64748b",
            flexShrink: 0,
            whiteSpace: "nowrap",
          }}
        >
          🗂️ Sin clasificar
          {sinDeposito > 0 && (
            <span style={{
              background: "#f97316", color: "#fff",
              fontSize: "0.62rem", fontWeight: 800,
              padding: "1px 5px", borderRadius: "999px",
              lineHeight: 1.4,
            }}>
              {sinDeposito}
            </span>
          )}
        </a>}

        {/* Link a Pagos con badge de pendientes — solo si puede actuar */}
        {!soloLectura && (
          <a
            href="/ventas/pagos"
            title="Pagos de pedidos despachados"
            style={{
              display: "flex", alignItems: "center", justifyContent: "center",
              gap: "5px",
              height: "32px",
              padding: "0 11px",
              background: pagosPendientes > 0 ? "#fffbeb" : "#f8fafc",
              border: `1px solid ${pagosPendientes > 0 ? "#fde68a" : "#e2e8f0"}`,
              borderRadius: "8px", textDecoration: "none",
              fontSize: "0.74rem", fontWeight: 700,
              color: pagosPendientes > 0 ? "#b45309" : "#64748b",
              flexShrink: 0,
              whiteSpace: "nowrap",
              position: "relative",
            }}
          >
            💰 Pagos
            {pagosPendientes > 0 && (
              <span style={{
                background: "#ef4444", color: "#fff",
                fontSize: "0.62rem", fontWeight: 800,
                padding: "1px 5px", borderRadius: "999px",
                lineHeight: 1.4,
              }}>
                {pagosPendientes}
              </span>
            )}
          </a>
        )}
      </div>

      {/* ── Kanban / Vista mobile ── */}
      {loading ? (
        <div style={{
          flex: 1, display: "flex",
          alignItems: "center", justifyContent: "center",
          color: "#94a3b8", fontSize: "0.9rem",
        }}>
          Cargando pedidos…
        </div>
      ) : isMobile ? (
        /* ── Vista mobile: secciones verticales en orden de flujo ── */
        <div style={{ flex: 1, overflowY: "auto", padding: "12px 14px" }}>
          {pipelineUnits.map(unit => {
            if (unit.type === "single") return renderMobileSection(unit.cfg);
            // Grupo "Despachado": banner + dos sub-secciones apiladas
            const total = (byEstado[unit.cargar.id] || []).length + (byEstado[unit.entregar.id] || []).length;
            if (total === 0) return null;
            return (
              <div key="group-despachado" style={{ marginBottom: "16px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "6px", padding: "8px 10px", background: "#dbeafe", border: "1.5px solid #85b7eb", borderRadius: "10px", marginBottom: "8px" }}>
                  <span style={{ fontSize: "0.85rem", lineHeight: 1 }}>📦</span>
                  <span style={{ flex: 1, fontWeight: 700, fontSize: "0.8rem", color: "#185fa5" }}>Despachado</span>
                  <span style={{ background: "#185fa522", color: "#185fa5", fontSize: "0.7rem", fontWeight: 700, padding: "1px 8px", borderRadius: "999px" }}>{total}</span>
                </div>
                <div style={{ paddingLeft: "8px", borderLeft: "2px solid #bae6fd" }}>
                  {renderMobileSection(unit.cargar, { labelOverride: "Cargar camión", forceShow: true })}
                  {renderMobileSection(unit.entregar, { labelOverride: "Entregar", forceShow: true })}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        /* ── Vista desktop: kanban horizontal ── */
        <div style={{
          flex: 1,
          display: "flex",
          gap: "10px",
          padding: "10px 16px",
          overflowX: "auto",
          alignItems: "stretch",
          boxSizing: "border-box",
        }}>
          {pipelineUnits.map(unit => {
            if (unit.type === "single") {
              return (
                <div key={unit.cfg.id} style={{ flex: "1 1 0", minWidth: "165px" }}>
                  <PipelineColumn
                    cfg={unit.cfg}
                    items={byEstado[unit.cfg.id] || []}
                    colHeight={colHeight}
                    onReasignar={puedeReasignarDeposito ? setReasignando : null}
                    pagosSet={pagosSet}
                  />
                </div>
              );
            }
            // Grupo "Despachado" con dos sub-columnas
            const cargarItems = byEstado[unit.cargar.id] || [];
            const entregarItems = byEstado[unit.entregar.id] || [];
            const total = cargarItems.length + entregarItems.length;
            return (
              <div key="group-despachado" style={{ flex: "2 1 0", minWidth: "340px", display: "flex", flexDirection: "column", height: colHeight }}>
                {/* Banner Despachado */}
                <div style={{ background: "#dbeafe", border: "1.5px solid #85b7eb", borderBottom: "none", borderRadius: "14px 14px 0 0", padding: "10px 14px", display: "flex", alignItems: "center", gap: "6px", flexShrink: 0 }}>
                  <span style={{ fontSize: "0.88rem", lineHeight: 1 }}>📦</span>
                  <span style={{ flex: 1, fontWeight: 700, fontSize: "0.82rem", color: "#185fa5" }}>Despachado</span>
                  <span style={{ background: total > 0 ? "#185fa522" : "#f1f5f9", color: total > 0 ? "#185fa5" : "#94a3b8", fontSize: "0.72rem", fontWeight: 700, padding: "1px 8px", borderRadius: "999px", minWidth: "22px", textAlign: "center" }}>{total}</span>
                </div>
                {/* Dos sub-columnas */}
                <div style={{ flex: 1, minHeight: 0, display: "flex", gap: "8px", padding: "8px", border: "1.5px solid #85b7eb", borderTop: "none", borderRadius: "0 0 14px 14px", background: "#f4f9fe" }}>
                  <div style={{ flex: "1 1 0", minWidth: 0 }}>
                    <PipelineColumn
                      cfg={unit.cargar}
                      items={cargarItems}
                      colHeight="100%"
                      labelOverride="Cargar camión"
                      groupedInner
                      onReasignar={puedeReasignarDeposito ? setReasignando : null}
                      pagosSet={pagosSet}
                    />
                  </div>
                  <div style={{ flex: "1 1 0", minWidth: 0 }}>
                    <PipelineColumn
                      cfg={unit.entregar}
                      items={entregarItems}
                      colHeight="100%"
                      labelOverride="Entregar"
                      groupedInner
                      onReasignar={puedeReasignarDeposito ? setReasignando : null}
                      pagosSet={pagosSet}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal reasignar depósito */}
      {reasignando && (
        <AsignarDepositoModal
          pedido={reasignando}
          titulo="Reasignar depósito"
          onClose={() => setReasignando(null)}
          onSaved={() => setReasignando(null)}
        />
      )}
    </div>
  );
}
